import { InjectedStores } from '~/modules/appx_site_schema';
import { routerStore } from '~/modules/stores/routerStore';
import { ERROR_CODES, IN_PLACE_ERROR_EVENT } from '~/modules/utilities/error_utilities';
import { logger } from './logger';

export interface RenderErrorInPlaceOptions {
    suppressLandingLog?: boolean;
}

/**
 * Swaps the mounted page component to the error page in place, WITHOUT
 * changing the URL. The error code is seeded onto `window.stores.ERROR.code`
 * — the same channel the server in-place render uses — because that is the
 * only source the error page's `errorCode` getter reads (query string or
 * `window.stores.ERROR.code`); it never reads the router match `data`. The
 * address bar keeps the original (errored) URL so the user can edit it.
 */
export async function renderErrorInPlace(
    code: string,
    shouldRender: () => boolean = () => true,
    options: RenderErrorInPlaceOptions = {},
): Promise<void> {
    const { default: ErrorPage } = await import('pages/error');
    // The route may change while the error-page chunk is loading. Revalidate immediately
    // before mutating shared state so a stale request cannot replace the destination page.
    if (!shouldRender()) {
        return;
    }
    const errorState = {
        code,
        ...(options.suppressLandingLog ? { suppressLandingLog: true } : {}),
    };
    if (window.stores) {
        // Normal path: the server always injects window.stores, so mutate in place — this keeps
        // FEATURE_FLAGS / SITE_CONFIG and only sets ERROR, with no unsafe cast.
        window.stores.ERROR = errorState;
    } else {
        // Defensive fallback (SSR/test harness where nothing was injected): seed a partial store.
        // The reader (error page errorCode getter) only optional-chains window.stores?.ERROR?.code,
        // so a partial is honest — don't pretend it's a full InjectedStores.
        (window as { stores?: Partial<InjectedStores> }).stores = {
            ERROR: errorState,
        };
    }
    // If the router reuses an already-mounted error page, update it reactively. When no error
    // page is mounted, the next instance consumes the window store seed during connection.
    window.dispatchEvent(new CustomEvent(IN_PLACE_ERROR_EVENT, { detail: errorState }));
    routerStore.endLoadingRoute(ErrorPage);
}

export function notFoundError(context?: string, shouldRender?: () => boolean) {
    logger.error(`Not found error${context ? `: ${context}` : ''}`);
    return renderErrorInPlace(ERROR_CODES.PAGE_NOT_FOUND, shouldRender);
}

export function missingQueryParamError(context?: string, shouldRender?: () => boolean) {
    logger.error(`Missing query param error${context ? `: ${context}` : ''}`);
    return renderErrorInPlace(ERROR_CODES.MISSING_QUERY_PARAM, shouldRender);
}

export function invalidQueryParamError(context?: string, shouldRender?: () => boolean) {
    logger.error(`Invalid query param error${context ? `: ${context}` : ''}`);
    return renderErrorInPlace(ERROR_CODES.INVALID_QUERY_PARAM, shouldRender);
}

export function missingListingError(context?: string) {
    logger.error(`Missing listing error${context ? `: ${context}` : ''}`);
    return renderErrorInPlace(ERROR_CODES.MISSING_LISTING);
}

// This utility is meant to help test when your LWC element being added is supposed to throw an
// exception. This can be used to wrap the document.body.appendChild() call, returning the
// exception that took place.
// Source - https://github.com/salesforce/lwc/blob/v2.45.2/packages/%40lwc/integration-karma/helpers/test-utils.js#L296-L315
// @ts-expect-error * Automated Type Error Fix
export function windowErrorListener(callback) {
    let error;
    // @ts-expect-error * Automated Type Error Fix
    function onError(event) {
        event.preventDefault(); // don't log the error
        error = event.error;
    }

    const originalOnError = window.onerror;
    Object.defineProperty(window, 'onerror', { value: null, configurable: true });
    window.addEventListener('error', onError);

    try {
        callback();
    } finally {
        Object.defineProperty(window, 'onerror', {
            value: originalOnError,
            configurable: true,
        });
        window.removeEventListener('error', onError);
    }
    return error;
}
